import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { usePageTitle } from "@/hooks/use-page-title";
import { useHideCrisp } from "@/hooks/use-hide-crisp";
import { DEFAULT_ACCENT_COLOR } from "@/lib/brand-colors";
import { useRoute } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Camera, Video, Upload, X, CheckCircle, Loader2, Square } from "lucide-react";
import { parseErrorMessage } from "@/lib/queryClient";
import { uploadMimeType } from "@/lib/upload-mime";
import { ErrorBannerWithId } from "@/components/error-id-chip";
import { computeVisibleFieldIds, isFieldEffectivelyRequired } from "@shared/booking-request-visibility";
import { renderInline, renderMarkdown } from "@/lib/booking-form-markdown";

type Field = {
  id: string;
  type: string;
  label: string;
  required: boolean;
  helpText?: string | null;
  placeholder?: string | null;
  options?: string[] | null;
  allowOther?: boolean | null;
  agreementText?: string | null;
  maxFiles?: number | null;
  maxDurationSeconds?: number | null;
  conditions?: Array<{ sourceFieldId: string; operator: string; values: unknown[] }> | null;
  conditionCombinator?: "all" | "any" | null;
  requiredWhenVisible?: boolean | null;
  page?: number | null;
};

const OTHER_VALUE = "__other__";

type FormData = {
  token: string;
  /** Listing property the submission will be filed under (validated server-side). */
  propertyId?: number | null;
  templateName: string;
  templateDescription?: string | null;
  fields: Field[];
  branding: {
    brandName?: string | null;
    brandLogoUrl?: string | null;
    accentColor?: string | null;
    headline?: string | null;
    description?: string | null;
    contactPhone?: string | null;
    contactEmail?: string | null;
    ctaButtonText?: string | null;
    footerText?: string | null;
  };
  propertyName?: string | null;
  /** True when the member identified by the link's `?m=` token already has a
   * pending/approved submission for this template — render the thank-you
   * screen instead of a fresh form. */
  alreadySubmitted?: boolean;
};

async function uploadFile(
  token: string,
  file: Blob,
  _filename: string,
  mimeType: string,
  onProgress?: (pct: number) => void,
): Promise<string> {
  const urlRes = await fetch(`/api/public/booking-request/${token}/upload-url`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  });
  if (!urlRes.ok) {
    const text = await urlRes.text();
    throw new Error(`${urlRes.status}: ${text}`);
  }
  const { uploadUrl, objectPath } = await urlRes.json();
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", mimeType || "application/octet-stream");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) { onProgress?.(100); resolve(); }
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Upload network error"));
    xhr.send(file);
  });
  return objectPath as string;
}

function PhotoField({ token, field, value, onChange }: { token: string; field: Field; value: string[]; onChange: (urls: string[]) => void }) {
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [capturing, setCapturing] = useState(false);
  // The uploaded object path (/objects/uploads/...) is private and not
  // servable on this public page, so render previews from local blob URLs
  // keyed by the stored path instead.
  const [localPreviews, setLocalPreviews] = useState<Record<string, string>>({});
  const localPreviewsRef = useRef<Record<string, string>>({});
  const max = field.maxFiles ?? 6;

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const setPreview = (url: string, file: Blob) => {
    localPreviewsRef.current[url] = URL.createObjectURL(file);
    setLocalPreviews({ ...localPreviewsRef.current });
  };

  const dropPreview = (url: string) => {
    const blobUrl = localPreviewsRef.current[url];
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      delete localPreviewsRef.current[url];
      setLocalPreviews({ ...localPreviewsRef.current });
    }
  };

  useEffect(() => () => {
    stopStream();
    Object.values(localPreviewsRef.current).forEach((u) => URL.revokeObjectURL(u));
  }, []);

  const uploadOne = async (file: File) => {
    setBusy(true);
    setProgress(0);
    try {
      const url = await uploadFile(token, file, file.name, uploadMimeType(file, "image/jpeg"), (p) => setProgress(p));
      setPreview(url, file);
      onChange([...value, url].slice(0, max));
    } catch (e: any) {
      alert(parseErrorMessage(e, "Upload failed"));
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const handleFiles = async (files: FileList | null, inputEl?: HTMLInputElement | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setProgress(0);
    try {
      const remaining = max - value.length;
      const list = Array.from(files).slice(0, remaining);
      const urls: string[] = [];
      for (const f of list) {
        const url = await uploadFile(token, f, f.name, uploadMimeType(f, "image/jpeg"), (p) => setProgress(p));
        setPreview(url, f);
        urls.push(url);
      }
      onChange([...value, ...urls]);
    } catch (e: any) {
      alert(parseErrorMessage(e, "Upload failed"));
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputEl) inputEl.value = "";
    }
  };

  const startCamera = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        cameraInputRef.current?.click();
        return;
      }
      if (typeof window !== "undefined" && window.isSecureContext === false) {
        alert("Camera access requires a secure (HTTPS) connection. Please open this page over HTTPS, or use the Upload option instead.");
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      setCapturing(true);
    } catch (e: any) {
      const name = e?.name || "";
      const inIframe = typeof window !== "undefined" && window.self !== window.top;
      let msg = "We couldn't access your camera. ";
      if (name === "NotAllowedError" || name === "SecurityError") {
        msg += inIframe
          ? "Camera is blocked in this embedded view. Open the form in a new browser tab and allow camera access when prompted, or use the Upload option."
          : "Please allow camera access in your browser's site permissions and try again, or use the Upload option.";
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        msg += "No camera was detected on this device. You can use the Upload option instead.";
      } else if (name === "NotReadableError") {
        msg += "Your camera is in use by another app. Please close other apps using the camera and try again.";
      } else {
        msg += e?.message || "Unknown error. You can use the Upload option instead.";
      }
      alert(msg);
    }
  };

  useEffect(() => {
    if (capturing && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [capturing]);

  const cancelCamera = () => {
    stopStream();
    setCapturing(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    stopStream();
    setCapturing(false);
    canvas.toBlob((blob) => {
      if (!blob) {
        alert("Failed to capture photo. Please try again or use the Upload option.");
        return;
      }
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" });
      void uploadOne(file);
    }, "image/jpeg", 0.92);
  };

  const atMax = value.length >= max;

  return (
    <div className="space-y-2">
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files, cameraInputRef.current)}
        data-testid={`input-photo-camera-${field.id}`}
      />
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files, uploadInputRef.current)}
        data-testid={`input-photo-upload-${field.id}`}
      />
      {capturing && (
        <div className="space-y-2">
          <div className="relative w-full">
            <video ref={videoRef} autoPlay muted playsInline className="w-full rounded bg-black aspect-video" data-testid={`video-photo-${field.id}`} />
            {/* Positioning guides: tall oval for the face, small ID-card rectangle lower-right */}
            <div className="pointer-events-none absolute inset-0" data-testid={`overlay-guides-${field.id}`}>
              <div
                className="absolute left-1/2 top-[8%] -translate-x-1/2 rounded-[50%] border-2 border-dashed border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.15)]"
                style={{ width: "26%", height: "66%" }}
              >
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white">
                  Your face
                </span>
              </div>
              <div
                className="absolute left-[60%] top-[62%] rounded-md border-2 border-dashed border-white/90"
                style={{ width: "26%", aspectRatio: "1.586" }}
              >
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white">
                  Your ID
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              className="flex-1 h-12 justify-center gap-2"
              onClick={capturePhoto}
              data-testid={`button-capture-photo-${field.id}`}
            >
              <Camera className="h-5 w-5" /> Capture
            </Button>
            <Button
              type="button"
              variant="outline"
              className="flex-1 h-12 justify-center gap-2"
              onClick={cancelCamera}
              data-testid={`button-cancel-camera-${field.id}`}
            >
              <X className="h-5 w-5" /> Cancel
            </Button>
          </div>
        </div>
      )}
      {!capturing && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1 h-14 justify-center gap-2"
            onClick={startCamera}
            disabled={busy || atMax}
            data-testid={`button-take-photo-${field.id}`}
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
            {busy ? `Uploading… ${progress}%` : "Take a photo"}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="flex-1 h-14 justify-center gap-2"
            onClick={() => uploadInputRef.current?.click()}
            disabled={busy || atMax}
            data-testid={`button-upload-photo-${field.id}`}
          >
            <Upload className="h-5 w-5" />
            {`Upload photo (${value.length}/${max})`}
          </Button>
        </div>
      )}
      {busy && <div className="h-1 w-full bg-gray-200 rounded overflow-hidden"><div className="h-full bg-blue-500 transition-all" style={{ width: `${progress}%` }} data-testid={`progress-photo-${field.id}`} /></div>}
      {value.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {value.map((url, idx) => (
            <div key={idx} className="relative aspect-square rounded overflow-hidden bg-gray-100">
              <img src={localPreviews[url] || url} alt="upload" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => { dropPreview(url); onChange(value.filter((_, i) => i !== idx)); }}
                className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"
                data-testid={`button-remove-photo-${field.id}-${idx}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function VideoField({ token, field, value, onChange, mode = "both" }: { token: string; field: Field; value: string[]; onChange: (urls: string[]) => void; mode?: "record" | "upload" | "both" }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const captureInputRef = useRef<HTMLInputElement | null>(null);
  const isCoarsePointer = typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
  const hasMediaRecorder = typeof window !== "undefined" && typeof (window as unknown as { MediaRecorder?: unknown }).MediaRecorder !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const max = field.maxFiles ?? 1;
  const maxDuration = field.maxDurationSeconds ?? 60;
  // The uploaded object path (/objects/uploads/...) is private and not servable
  // on this public page, so play back previews from local blob URLs keyed by the
  // stored path instead.
  const [localPreviews, setLocalPreviews] = useState<Record<string, string>>({});
  const localPreviewsRef = useRef<Record<string, string>>({});

  const setPreview = (url: string, file: Blob) => {
    localPreviewsRef.current[url] = URL.createObjectURL(file);
    setLocalPreviews({ ...localPreviewsRef.current });
  };

  const dropPreview = (url: string) => {
    const blobUrl = localPreviewsRef.current[url];
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      delete localPreviewsRef.current[url];
      setLocalPreviews({ ...localPreviewsRef.current });
    }
  };

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => () => {
    Object.values(localPreviewsRef.current).forEach((u) => URL.revokeObjectURL(u));
  }, []);

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        alert("Your browser doesn't support in-app recording. Please use the Upload option instead, or try a modern browser like Chrome or Safari.");
        return;
      }
      if (typeof window !== "undefined" && window.isSecureContext === false) {
        alert("Camera access requires a secure (HTTPS) connection. Please open this page over HTTPS and try again.");
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: true });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      } else {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        return;
      }
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9") ? "video/webm;codecs=vp9" : "video/webm";
      const rec = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stopStream();
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        setBusy(true);
        setProgress(0);
        try {
          const url = await uploadFile(token, blob, `recording-${Date.now()}.webm`, "video/webm", (p) => setProgress(p));
          setPreview(url, blob);
          onChange([...value, url].slice(0, max));
        } catch (e: unknown) {
          alert(parseErrorMessage(e, "Upload failed"));
        } finally {
          setBusy(false);
          setProgress(0);
        }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
      setSeconds(0);
    } catch (e: any) {
      const name = e?.name || "";
      const inIframe = typeof window !== "undefined" && window.self !== window.top;
      let msg = "We couldn't access your camera. ";
      if (name === "NotAllowedError" || name === "SecurityError") {
        msg += inIframe
          ? "Camera is blocked in this embedded view. Open the form in a new browser tab and allow camera access when prompted."
          : "Please allow camera and microphone access in your browser's site permissions and try again.";
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        msg += "No camera was detected on this device. You can use the Upload option instead.";
      } else if (name === "NotReadableError") {
        msg += "Your camera is in use by another app. Please close other apps using the camera and try again.";
      } else {
        msg += e?.message || "Unknown error. You can use the Upload option instead.";
      }
      alert(msg);
    }
  };

  useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => {
      setSeconds((s) => {
        const n = s + 1;
        if (n >= maxDuration) {
          recorderRef.current?.stop();
          setRecording(false);
        }
        return n;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [recording, maxDuration]);

  const stopRecording = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const probeDuration = (file: File): Promise<number> => new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(isFinite(v.duration) ? v.duration : 0); };
      v.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
      v.src = url;
    } catch { resolve(0); }
  });

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setProgress(0);
    try {
      const list = Array.from(files).slice(0, max - value.length);
      for (const f of list) {
        const dur = await probeDuration(f);
        if (dur > maxDuration + 0.5) {
          alert(`Video is ${Math.round(dur)}s long. Maximum allowed is ${maxDuration}s.`);
          return;
        }
      }
      const urls: string[] = [];
      for (const f of list) {
        const url = await uploadFile(token, f, f.name, uploadMimeType(f, "video/mp4"), (p) => setProgress(p));
        setPreview(url, f);
        urls.push(url);
      }
      onChange([...value, ...urls]);
    } catch (e: any) {
      alert(parseErrorMessage(e, "Upload failed"));
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`input-video-${field.id}`}
      />
      <input
        ref={captureInputRef}
        type="file"
        accept="video/*"
        capture="user"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`input-video-capture-${field.id}`}
      />
      <div className={recording ? "space-y-2" : "hidden"}>
        <video ref={videoRef} autoPlay muted playsInline className="w-full rounded bg-black aspect-video" />
        <div className="flex items-center justify-between">
          <span className="text-sm text-red-600 font-medium">● Recording {seconds}s / {maxDuration}s</span>
          <Button type="button" onClick={stopRecording} variant="destructive" data-testid={`button-stop-record-${field.id}`}>
            <Square className="h-4 w-4 mr-1" /> Stop
          </Button>
        </div>
      </div>
      {busy && <div className="space-y-1"><div className="text-xs text-gray-600">Uploading video… {progress}%</div><div className="h-1 w-full bg-gray-200 rounded overflow-hidden"><div className="h-full bg-blue-500 transition-all" style={{ width: `${progress}%` }} data-testid={`progress-video-${field.id}`} /></div></div>}
      {!recording && value.length < max && (
        <div className="space-y-2">
          <div className={`grid gap-2 ${mode === "both" ? "grid-cols-2" : "grid-cols-1"}`}>
            {(mode === "record" || mode === "both") && (
              <Button
                type="button"
                variant="outline"
                className="h-14 gap-2"
                onClick={() => {
                  if (isCoarsePointer || !hasMediaRecorder) captureInputRef.current?.click();
                  else void startRecording();
                }}
                disabled={busy}
                data-testid={`button-record-video-${field.id}`}
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Video className="h-5 w-5" />}
                Record
              </Button>
            )}
            {(mode === "upload" || mode === "both") && (
              <Button
                type="button"
                variant="outline"
                className="h-14 gap-2"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                data-testid={`button-upload-video-${field.id}`}
              >
                <Upload className="h-5 w-5" />
                Upload
              </Button>
            )}
          </div>
          {mode === "record" && !isCoarsePointer && !hasMediaRecorder && (
            <p className="text-xs text-gray-500">Your browser does not support in-app recording. Please record on your device and upload the video.</p>
          )}
        </div>
      )}
      {value.length > 0 && (
        <div className="space-y-2">
          {value.map((url, idx) => (
            <div key={idx} className="relative">
              <video src={localPreviews[url] || url} controls playsInline className="w-full rounded bg-black" data-testid={`video-preview-${field.id}-${idx}`} />
              <button
                type="button"
                onClick={() => { dropPreview(url); onChange(value.filter((_, i) => i !== idx)); }}
                className="absolute top-2 right-2 bg-black/70 text-white rounded-full p-1"
                data-testid={`button-remove-video-${field.id}-${idx}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SignatureField({ token, field, value, onChange }: { token: string; field: Field; value: string[]; onChange: (urls: string[]) => void }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [busy, setBusy] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#111";
    ctx.lineCap = "round";
  }, []);

  const getPos = (e: any) => {
    const c = canvasRef.current!;
    const rect = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x: x * (c.width / rect.width), y: y * (c.height / rect.height) };
  };

  const start = (e: any) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e: any) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c?.getContext("2d");
    if (c && ctx) {
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, c.width, c.height);
    }
    setHasInk(false);
    onChange([]);
  };

  const save = async () => {
    const c = canvasRef.current;
    if (!c) return;
    setBusy(true);
    try {
      const blob: Blob = await new Promise((resolve, reject) =>
        c.toBlob((b) => (b ? resolve(b) : reject(new Error("Failed"))), "image/png")
      );
      const url = await uploadFile(token, blob, `signature-${Date.now()}.png`, "image/png");
      onChange([url]);
    } catch (e: unknown) {
      alert(parseErrorMessage(e, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  if (value.length > 0) {
    return (
      <div className="space-y-2">
        <img src={value[0]} alt="signature" className="border rounded bg-white max-h-32" />
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([])} data-testid={`button-clear-signature-${field.id}`}>
          Clear and re-sign
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={600}
        height={200}
        className="border rounded bg-white touch-none w-full h-40"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid={`canvas-signature-${field.id}`}
      />
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={clear} data-testid={`button-clear-signature-${field.id}`}>Clear</Button>
        <Button type="button" size="sm" onClick={save} disabled={!hasInk || busy} data-testid={`button-save-signature-${field.id}`}>
          {busy ? "Saving…" : "Save signature"}
        </Button>
      </div>
    </div>
  );
}

function FileField({ token, field, value, onChange }: { token: string; field: Field; value: string[]; onChange: (urls: string[]) => void }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const max = field.maxFiles ?? 3;

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const list = Array.from(files).slice(0, max - value.length);
      const urls: string[] = [];
      for (const f of list) {
        const url = await uploadFile(token, f, f.name, uploadMimeType(f, "application/octet-stream"));
        urls.push(url);
      }
      onChange([...value, ...urls]);
    } catch (e: any) {
      alert(parseErrorMessage(e, "Upload failed"));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid={`input-file-${field.id}`} />
      <Button type="button" variant="outline" className="w-full h-12 gap-2" onClick={() => inputRef.current?.click()} disabled={busy || value.length >= max} data-testid={`button-add-file-${field.id}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        Choose files ({value.length}/{max})
      </Button>
      {value.map((url, idx) => (
        <div key={idx} className="flex items-center justify-between text-sm border rounded px-2 py-1 bg-gray-50">
          {/* The stored path is a private object URL the applicant can't
              open (this page is unauthenticated), so render it as plain text
              instead of a link that 401s. */}
          <span className="truncate flex-1 mr-2">{url.split("/").pop()}</span>
          <button type="button" onClick={() => onChange(value.filter((_, i) => i !== idx))} aria-label="remove">
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  );
}

export default function BookingRequestPage() {
  useHideCrisp();
  const [, params] = useRoute("/booking-request/:token");
  const token = params?.token || "";
  // Task #4594 — signed member-identity token baked into the link when it was
  // auto-sent to a specific member. Read once from the URL (wouter's location
  // omits the query string) and echoed back on submit so the submission links
  // to the exact member.
  const [memberRef] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("m");
  });
  // Option B — a direct booking listing opens the host's general form with
  // `?property=<id>` so the submission is filed under that listing. The
  // server validates it; only the id it echoes back is submitted.
  const [requestedProperty] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = new URLSearchParams(window.location.search).get("property");
    return raw && /^\d+$/.test(raw) ? raw : null;
  });
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [otherText, setOtherText] = useState<Record<string, string>>({});
  const [mediaUrls, setMediaUrls] = useState<Record<string, string[]>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const { data, isLoading, isError } = useQuery<FormData>({
    queryKey: ["/api/public/booking-request", token, memberRef, requestedProperty],
    queryFn: async () => {
      // Echo the signed member token so the server can tell us this member
      // already submitted (re-clicking the link must not show a blank form).
      const params = new URLSearchParams();
      if (memberRef) params.set("m", memberRef);
      if (requestedProperty) params.set("property", requestedProperty);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await fetch(`/api/public/booking-request/${token}${qs}`);
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`${res.status}: ${text}`);
      }
      return res.json();
    },
    enabled: !!token,
    retry: false,
  });

  usePageTitle(data ? `${data.templateName} · ${data.branding.brandName}` : "Booking Request");

  const visibleFieldIds = useMemo(
    () => data ? computeVisibleFieldIds(data.fields, responses) : new Set<string>(),
    [data, responses],
  );

  const pageNumbers = useMemo(() => {
    if (!data) return [1];
    const s = new Set<number>();
    for (const f of data.fields) s.add(f.page ?? 1);
    return Array.from(s).sort((a, b) => a - b);
  }, [data]);

  // Pages that currently have at least one visible field (empty pages are skipped).
  const activePages = useMemo(() => {
    if (!data) return [1];
    const ap = pageNumbers.filter((p) =>
      data.fields.some((f) => (f.page ?? 1) === p && visibleFieldIds.has(f.id)),
    );
    return ap.length > 0 ? ap : [pageNumbers[0] ?? 1];
  }, [data, pageNumbers, visibleFieldIds]);

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (isError || !data) {
    return <div className="min-h-screen flex items-center justify-center px-6 text-center"><div><h1 className="text-xl font-semibold">Form not available</h1><p className="text-gray-500 mt-2">This link is invalid or has been disabled.</p></div></div>;
  }

  const accent = data.branding.accentColor || DEFAULT_ACCENT_COLOR;

  const effectivePage = activePages.includes(currentPage) ? currentPage : activePages[0];
  const pageIdx = activePages.indexOf(effectivePage);
  const isLastPage = pageIdx >= activePages.length - 1;
  const isFirstPage = pageIdx <= 0;
  const isMultiPage = activePages.length > 1;
  const currentPageFields = data.fields.filter(
    (f) => (f.page ?? 1) === effectivePage && visibleFieldIds.has(f.id),
  );

  const buildResponsesForSubmit = (): Record<string, any> => {
    const out: Record<string, any> = {};
    for (const f of data.fields) {
      if (!visibleFieldIds.has(f.id)) continue;
      const raw = responses[f.id];
      if (raw === undefined) continue;
      if (!f.allowOther) { out[f.id] = raw; continue; }
      if (f.type === "radio" || f.type === "select") {
        out[f.id] = raw === OTHER_VALUE ? (otherText[f.id] || "").trim() : raw;
      } else if (f.type === "multiselect") {
        const arr: string[] = Array.isArray(raw) ? raw : [];
        if (arr.includes(OTHER_VALUE)) {
          const txt = (otherText[f.id] || "").trim();
          out[f.id] = arr.map((v) => v === OTHER_VALUE ? txt : v).filter((v) => v !== "");
        } else {
          out[f.id] = arr;
        }
      } else {
        out[f.id] = raw;
      }
    }
    return out;
  };

  const otherIsPending = (f: Field): boolean => {
    if (!f.allowOther) return false;
    if (f.type === "radio" || f.type === "select") {
      if (responses[f.id] !== OTHER_VALUE) return false;
      return (otherText[f.id] || "").trim() === "";
    }
    if (f.type === "multiselect") {
      const arr: string[] = Array.isArray(responses[f.id]) ? responses[f.id] : [];
      if (!arr.includes(OTHER_VALUE)) return false;
      return (otherText[f.id] || "").trim() === "";
    }
    return false;
  };

  if (submitted || data.alreadySubmitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-md mx-auto p-6 pt-12 text-center">
          <CheckCircle className="h-16 w-16 mx-auto" style={{ color: accent }} />
          <h1 className="text-2xl font-bold mt-4" data-testid="text-thank-you">Thank you for submitting!</h1>
          <p className="text-gray-600 mt-2">Your submission will be reviewed and we will be in contact for next steps or if anything further is needed.</p>
          {data.branding.contactEmail && (
            <p className="text-sm text-gray-500 mt-6">Questions? Email <a className="underline" href={`mailto:${data.branding.contactEmail}`}>{data.branding.contactEmail}</a></p>
          )}
        </div>
      </div>
    );
  }

  // Returns the first validation error among the given fields, or null if all pass.
  const firstError = (subset: Field[]): string | null => {
    for (const f of subset) {
      if (!isFieldEffectivelyRequired(f as any, visibleFieldIds)) continue;
      if (["photo", "video_record", "video_upload", "file", "signature"].includes(f.type)) {
        const arr = mediaUrls[f.id];
        if (!arr || arr.length === 0) return `Required: ${f.label}`;
      } else if (f.type === "agreement" || f.type === "yesno") {
        const v = responses[f.id];
        if (f.type === "agreement" && v !== true) return `You must accept: ${f.label}`;
        if (f.type === "yesno" && v !== true && v !== false) return `Required: ${f.label}`;
      } else if (f.type === "multiselect") {
        const v = responses[f.id];
        if (!Array.isArray(v) || v.length === 0) return `Required: ${f.label}`;
      } else {
        const v = responses[f.id];
        if (v == null || (typeof v === "string" && v.trim() === "")) return `Required: ${f.label}`;
      }
    }
    for (const f of subset) {
      if (!visibleFieldIds.has(f.id)) continue;
      if (otherIsPending(f)) return `Please specify "Other" for: ${f.label}`;
    }
    return null;
  };

  // True when every visible field in the subset is satisfied (drives button enabled state).
  const subsetComplete = (subset: Field[]): boolean => {
    if (subset.some((f) => visibleFieldIds.has(f.id) && otherIsPending(f))) return false;
    return subset.every((f) => {
      if (!isFieldEffectivelyRequired(f as any, visibleFieldIds)) return true;
      if (["photo", "video_record", "video_upload", "file", "signature"].includes(f.type)) return (mediaUrls[f.id]?.length || 0) > 0;
      if (f.type === "agreement") return responses[f.id] === true;
      if (f.type === "yesno") return responses[f.id] === true || responses[f.id] === false;
      if (f.type === "multiselect") return Array.isArray(responses[f.id]) && responses[f.id].length > 0;
      const v = responses[f.id];
      return v != null && (typeof v !== "string" || v.trim() !== "");
    });
  };

  const goToPage = (p: number) => {
    setCurrentPage(p);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goNext = () => {
    setError(null);
    const err = firstError(currentPageFields);
    if (err) { setError(err); return; }
    const next = activePages[pageIdx + 1];
    if (next != null) goToPage(next);
  };

  const goBack = () => {
    setError(null);
    const prev = activePages[pageIdx - 1];
    if (prev != null) goToPage(prev);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // Defense-in-depth: validate all visible fields (across every page) before submit.
    const err = firstError(data.fields);
    if (err) { setError(err); return; }
    setSubmitting(true);
    try {
      const visibleMediaUrls: Record<string, string[]> = {};
      for (const f of data.fields) {
        if (visibleFieldIds.has(f.id) && mediaUrls[f.id]) visibleMediaUrls[f.id] = mediaUrls[f.id];
      }
      const res = await fetch(`/api/public/booking-request/${token}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ responses: buildResponsesForSubmit(), mediaUrls: visibleMediaUrls, memberRef: memberRef || undefined, propertyId: data?.propertyId ?? undefined }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`${res.status}: ${text}`);
      }
      setSubmitted(true);
    } catch (e: any) {
      setError(parseErrorMessage(e, "Submit failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-md mx-auto bg-white min-h-screen shadow-sm">
        <div className="p-6 text-center" style={{ backgroundColor: accent + "10" }}>
          {data.branding.brandLogoUrl && (
            <img src={data.branding.brandLogoUrl} alt="Logo" className="h-12 mx-auto mb-3 object-contain" />
          )}
          {data.branding.brandName && <p className="text-sm font-medium" style={{ color: accent }} data-testid="text-brand-name">{data.branding.brandName}</p>}
          <h1 className="text-2xl font-bold mt-1" data-testid="text-headline">{data.branding.headline || data.templateName}</h1>
          {data.propertyName && <p className="text-sm text-gray-600 mt-1" data-testid="text-property-name">{data.propertyName}</p>}
          {data.branding.description && (
            <div className="text-sm text-gray-600 mt-3 text-left space-y-1" data-testid="text-description">
              {renderMarkdown(data.branding.description)}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {isMultiPage && (
            <div className="flex items-center justify-between text-xs text-gray-500" data-testid="text-page-indicator">
              <span>Page {pageIdx + 1} of {activePages.length}</span>
              <div className="flex gap-1">
                {activePages.map((_, i) => (
                  <span
                    key={i}
                    className="h-1.5 w-6 rounded-full"
                    style={{ backgroundColor: i <= pageIdx ? accent : "#e5e7eb" }}
                  />
                ))}
              </div>
            </div>
          )}
          {currentPageFields.map((f) => (
            <div key={f.id} className="space-y-1">
              {f.type !== "agreement" && (
                <Label className="font-medium whitespace-pre-line">
                  {renderMarkdown(f.label)}{isFieldEffectivelyRequired(f as any, visibleFieldIds) && <span className="text-red-500"> *</span>}
                </Label>
              )}
              {f.helpText && f.type !== "agreement" && <p className="text-xs text-gray-500">{f.helpText}</p>}

              {f.type === "text" && (
                <Input
                  value={responses[f.id] || ""}
                  onChange={(e) => setResponses({ ...responses, [f.id]: e.target.value })}
                  placeholder={f.placeholder || ""}
                  data-testid={`input-${f.id}`}
                />
              )}
              {f.type === "longtext" && (
                <Textarea
                  value={responses[f.id] || ""}
                  onChange={(e) => setResponses({ ...responses, [f.id]: e.target.value })}
                  placeholder={f.placeholder || ""}
                  rows={4}
                  data-testid={`input-${f.id}`}
                />
              )}
              {f.type === "email" && (
                <Input type="email" value={responses[f.id] || ""} onChange={(e) => setResponses({ ...responses, [f.id]: e.target.value })} placeholder={f.placeholder || "you@example.com"} data-testid={`input-${f.id}`} />
              )}
              {f.type === "phone" && (
                <Input type="tel" value={responses[f.id] || ""} onChange={(e) => setResponses({ ...responses, [f.id]: e.target.value })} placeholder={f.placeholder || "(555) 555-5555"} data-testid={`input-${f.id}`} />
              )}
              {f.type === "number" && (
                <Input type="number" value={responses[f.id] ?? ""} onChange={(e) => setResponses({ ...responses, [f.id]: e.target.value })} placeholder={f.placeholder || ""} data-testid={`input-${f.id}`} />
              )}
              {f.type === "date" && (
                <Input type="date" value={responses[f.id] || ""} onChange={(e) => setResponses({ ...responses, [f.id]: e.target.value })} data-testid={`input-${f.id}`} />
              )}
              {f.type === "select" && (
                <div className="space-y-2">
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={responses[f.id] || ""}
                    onChange={(e) => setResponses({ ...responses, [f.id]: e.target.value })}
                    data-testid={`select-${f.id}`}
                  >
                    <option value="">Select…</option>
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    {f.allowOther && <option value={OTHER_VALUE}>Other (please specify)</option>}
                  </select>
                  {f.allowOther && responses[f.id] === OTHER_VALUE && (
                    <Input
                      value={otherText[f.id] || ""}
                      onChange={(e) => setOtherText({ ...otherText, [f.id]: e.target.value })}
                      placeholder="Please specify"
                      data-testid={`input-other-${f.id}`}
                    />
                  )}
                </div>
              )}
              {f.type === "radio" && (
                <div className="space-y-2">
                  {(f.options || []).map((o) => (
                    <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name={`radio-${f.id}`}
                        value={o}
                        checked={responses[f.id] === o}
                        onChange={() => setResponses({ ...responses, [f.id]: o })}
                        className="h-4 w-4"
                        data-testid={`radio-${f.id}-${o}`}
                      />
                      {o}
                    </label>
                  ))}
                  {f.allowOther && (
                    <>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name={`radio-${f.id}`}
                          value={OTHER_VALUE}
                          checked={responses[f.id] === OTHER_VALUE}
                          onChange={() => setResponses({ ...responses, [f.id]: OTHER_VALUE })}
                          className="h-4 w-4"
                          data-testid={`radio-${f.id}-other`}
                        />
                        Other (please specify)
                      </label>
                      {responses[f.id] === OTHER_VALUE && (
                        <Input
                          value={otherText[f.id] || ""}
                          onChange={(e) => setOtherText({ ...otherText, [f.id]: e.target.value })}
                          placeholder="Please specify"
                          data-testid={`input-other-${f.id}`}
                        />
                      )}
                    </>
                  )}
                </div>
              )}
              {f.type === "yesno" && (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={responses[f.id] === true ? "default" : "outline"}
                    onClick={() => setResponses({ ...responses, [f.id]: true })}
                    className="flex-1"
                    data-testid={`button-yes-${f.id}`}
                  >Yes</Button>
                  <Button
                    type="button"
                    variant={responses[f.id] === false ? "default" : "outline"}
                    onClick={() => setResponses({ ...responses, [f.id]: false })}
                    className="flex-1"
                    data-testid={`button-no-${f.id}`}
                  >No</Button>
                </div>
              )}
              {f.type === "multiselect" && (
                <div className="space-y-1">
                  {(f.options || []).map((o) => {
                    const arr: string[] = Array.isArray(responses[f.id]) ? responses[f.id] : [];
                    const checked = arr.includes(o);
                    return (
                      <label key={o} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            const next = v ? [...arr, o] : arr.filter((x) => x !== o);
                            setResponses({ ...responses, [f.id]: next });
                          }}
                          data-testid={`checkbox-${f.id}-${o}`}
                        />
                        {o}
                      </label>
                    );
                  })}
                  {f.allowOther && (() => {
                    const arr: string[] = Array.isArray(responses[f.id]) ? responses[f.id] : [];
                    const checked = arr.includes(OTHER_VALUE);
                    return (
                      <>
                        <label className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              const next = v ? [...arr, OTHER_VALUE] : arr.filter((x) => x !== OTHER_VALUE);
                              setResponses({ ...responses, [f.id]: next });
                            }}
                            data-testid={`checkbox-${f.id}-other`}
                          />
                          Other (please specify)
                        </label>
                        {checked && (
                          <Input
                            value={otherText[f.id] || ""}
                            onChange={(e) => setOtherText({ ...otherText, [f.id]: e.target.value })}
                            placeholder="Please specify"
                            data-testid={`input-other-${f.id}`}
                          />
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
              {f.type === "photo" && (
                <PhotoField token={token} field={f} value={mediaUrls[f.id] || []} onChange={(v) => setMediaUrls({ ...mediaUrls, [f.id]: v })} />
              )}
              {f.type === "video_record" && (
                <VideoField token={token} field={f} value={mediaUrls[f.id] || []} onChange={(v) => setMediaUrls({ ...mediaUrls, [f.id]: v })} mode="record" />
              )}
              {f.type === "video_upload" && (
                <VideoField token={token} field={f} value={mediaUrls[f.id] || []} onChange={(v) => setMediaUrls({ ...mediaUrls, [f.id]: v })} mode="upload" />
              )}
              {f.type === "signature" && (
                <SignatureField token={token} field={f} value={mediaUrls[f.id] || []} onChange={(v) => setMediaUrls({ ...mediaUrls, [f.id]: v })} />
              )}
              {f.type === "file" && (
                <FileField token={token} field={f} value={mediaUrls[f.id] || []} onChange={(v) => setMediaUrls({ ...mediaUrls, [f.id]: v })} />
              )}
              {f.type === "agreement" && (
                <div className="border rounded-md p-3 bg-gray-50 space-y-2">
                  <div className="text-sm text-gray-700 space-y-1">{renderMarkdown(f.agreementText || f.helpText || "")}</div>
                  <label className="flex items-start gap-2 text-sm font-medium">
                    <Checkbox
                      checked={responses[f.id] === true}
                      onCheckedChange={(v) => setResponses({ ...responses, [f.id]: v === true })}
                      className="mt-0.5"
                      data-testid={`checkbox-agreement-${f.id}`}
                    />
                    <span className="whitespace-pre-line">{renderMarkdown(f.label)}{f.required && <span className="text-red-500"> *</span>}</span>
                  </label>
                </div>
              )}
            </div>
          ))}

          {error && (
            <ErrorBannerWithId message={error} testId="text-form-error" />
          )}

          <div className="flex gap-2 sticky bottom-3 z-10">
            {isMultiPage && !isFirstPage && (
              <Button
                type="button"
                variant="outline"
                className="h-12 text-base shadow-lg bg-white"
                onClick={goBack}
                disabled={submitting}
                data-testid="button-back-booking-request"
              >
                Back
              </Button>
            )}
            {isMultiPage && !isLastPage ? (
              <Button
                type="button"
                className="flex-1 h-12 text-base shadow-lg"
                onClick={goNext}
                disabled={!subsetComplete(currentPageFields)}
                style={{ backgroundColor: accent }}
                data-testid="button-next-booking-request"
              >
                Next
              </Button>
            ) : (
              <Button
                type="submit"
                className="flex-1 h-12 text-base shadow-lg"
                disabled={submitting || !subsetComplete(currentPageFields)}
                style={{ backgroundColor: accent }}
                data-testid="button-submit-booking-request"
              >
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</> : (data.branding.ctaButtonText || "Submit Request")}
              </Button>
            )}
          </div>

          {(data.branding.contactPhone || data.branding.contactEmail) && (
            <p className="text-xs text-gray-500 text-center pt-2">
              Need help?
              {data.branding.contactPhone && <> Call <a href={`tel:${data.branding.contactPhone}`} className="underline">{data.branding.contactPhone}</a></>}
              {data.branding.contactEmail && <> · <a href={`mailto:${data.branding.contactEmail}`} className="underline">{data.branding.contactEmail}</a></>}
            </p>
          )}
          {data.branding.footerText && <p className="text-xs text-gray-400 text-center pt-2">{data.branding.footerText}</p>}
        </form>
      </div>
    </div>
  );
}
